/**
 * Fonte di verità unica per i paesi: nome italiano (identico ai valori storici
 * salvati in clients.country), codice ISO 3166-1 alpha-2, prefisso telefonico,
 * appartenenza UE e label adattive per indirizzo/fiscale.
 *
 * Sostituisce le copie di AVAILABLE_COUNTRIES (AddressStep,
 * OperationalLocationsManager) e la countryMap interna di AddressAutocomplete.
 * ATTENZIONE: i nomi NON vanno rinominati — il backfill di clients.country_iso
 * (migration 20260813) mappa esattamente queste stringhe.
 */

export interface CountryInfo {
  iso: string;
  name: string;
  phonePrefix: string;
  eu: boolean;
  adminLabel: string;
  postalLabel: string;
  /** Prefisso VIES per i paesi UE (GR → EL); null dove il VIES non si applica. */
  vatPrefix: string | null;
}

/** [iso, nome italiano, prefisso telefonico] */
type Row = [string, string, string];

const ROWS: Row[] = [
  ['AF', 'Afghanistan', '+93'], ['AL', 'Albania', '+355'], ['DZ', 'Algeria', '+213'],
  ['AD', 'Andorra', '+376'], ['AO', 'Angola', '+244'], ['AG', 'Antigua e Barbuda', '+1268'],
  ['AR', 'Argentina', '+54'], ['AM', 'Armenia', '+374'], ['AU', 'Australia', '+61'],
  ['AT', 'Austria', '+43'], ['AZ', 'Azerbaigian', '+994'], ['BS', 'Bahamas', '+1242'],
  ['BH', 'Bahrain', '+973'], ['BD', 'Bangladesh', '+880'], ['BB', 'Barbados', '+1246'],
  ['BE', 'Belgio', '+32'], ['BZ', 'Belize', '+501'], ['BJ', 'Benin', '+229'],
  ['BT', 'Bhutan', '+975'], ['BY', 'Bielorussia', '+375'], ['BO', 'Bolivia', '+591'],
  ['BA', 'Bosnia ed Erzegovina', '+387'], ['BW', 'Botswana', '+267'], ['BR', 'Brasile', '+55'],
  ['BN', 'Brunei', '+673'], ['BG', 'Bulgaria', '+359'], ['BF', 'Burkina Faso', '+226'],
  ['BI', 'Burundi', '+257'], ['KH', 'Cambogia', '+855'], ['CM', 'Camerun', '+237'],
  ['CA', 'Canada', '+1'], ['CV', 'Capo Verde', '+238'], ['TD', 'Ciad', '+235'],
  ['CL', 'Cile', '+56'], ['CN', 'Cina', '+86'], ['CY', 'Cipro', '+357'],
  ['CO', 'Colombia', '+57'], ['KM', 'Comore', '+269'], ['CG', 'Congo', '+242'],
  ['KP', 'Corea del Nord', '+850'], ['KR', 'Corea del Sud', '+82'], ['CR', 'Costa Rica', '+506'],
  ["CI", "Costa d'Avorio", '+225'], ['HR', 'Croazia', '+385'], ['CU', 'Cuba', '+53'],
  ['DK', 'Danimarca', '+45'], ['DM', 'Dominica', '+1767'], ['EC', 'Ecuador', '+593'],
  ['EG', 'Egitto', '+20'], ['SV', 'El Salvador', '+503'], ['AE', 'Emirati Arabi Uniti', '+971'],
  ['ER', 'Eritrea', '+291'], ['EE', 'Estonia', '+372'], ['SZ', 'Eswatini', '+268'],
  ['ET', 'Etiopia', '+251'], ['FJ', 'Figi', '+679'], ['PH', 'Filippine', '+63'],
  ['FI', 'Finlandia', '+358'], ['FR', 'Francia', '+33'], ['GA', 'Gabon', '+241'],
  ['GM', 'Gambia', '+220'], ['GE', 'Georgia', '+995'], ['DE', 'Germania', '+49'],
  ['GH', 'Ghana', '+233'], ['JM', 'Giamaica', '+1876'], ['JP', 'Giappone', '+81'],
  ['DJ', 'Gibuti', '+253'], ['JO', 'Giordania', '+962'], ['GR', 'Grecia', '+30'],
  ['GD', 'Grenada', '+1473'], ['GT', 'Guatemala', '+502'], ['GN', 'Guinea', '+224'],
  ['GW', 'Guinea-Bissau', '+245'], ['GQ', 'Guinea Equatoriale', '+240'], ['GY', 'Guyana', '+592'],
  ['HT', 'Haiti', '+509'], ['HN', 'Honduras', '+504'], ['IN', 'India', '+91'],
  ['ID', 'Indonesia', '+62'], ['IR', 'Iran', '+98'], ['IQ', 'Iraq', '+964'],
  ['IE', 'Irlanda', '+353'], ['IS', 'Islanda', '+354'], ['IL', 'Israele', '+972'],
  ['IT', 'Italia', '+39'], ['KZ', 'Kazakistan', '+7'], ['KE', 'Kenya', '+254'],
  ['KG', 'Kirghizistan', '+996'], ['KI', 'Kiribati', '+686'], ['KW', 'Kuwait', '+965'],
  ['LA', 'Laos', '+856'], ['LS', 'Lesotho', '+266'], ['LV', 'Lettonia', '+371'],
  ['LB', 'Libano', '+961'], ['LR', 'Liberia', '+231'], ['LY', 'Libia', '+218'],
  ['LI', 'Liechtenstein', '+423'], ['LT', 'Lituania', '+370'], ['LU', 'Lussemburgo', '+352'],
  ['MG', 'Madagascar', '+261'], ['MW', 'Malawi', '+265'], ['MY', 'Malaysia', '+60'],
  ['MV', 'Maldive', '+960'], ['ML', 'Mali', '+223'], ['MT', 'Malta', '+356'],
  ['MA', 'Marocco', '+212'], ['MH', 'Isole Marshall', '+692'], ['MR', 'Mauritania', '+222'],
  ['MU', 'Mauritius', '+230'], ['MX', 'Messico', '+52'], ['FM', 'Micronesia', '+691'],
  ['MD', 'Moldavia', '+373'], ['MC', 'Monaco', '+377'], ['MN', 'Mongolia', '+976'],
  ['ME', 'Montenegro', '+382'], ['MZ', 'Mozambico', '+258'], ['MM', 'Myanmar', '+95'],
  ['NA', 'Namibia', '+264'], ['NR', 'Nauru', '+674'], ['NP', 'Nepal', '+977'],
  ['NI', 'Nicaragua', '+505'], ['NE', 'Niger', '+227'], ['NG', 'Nigeria', '+234'],
  ['MK', 'Macedonia del Nord', '+389'], ['NO', 'Norvegia', '+47'], ['NZ', 'Nuova Zelanda', '+64'],
  ['OM', 'Oman', '+968'], ['NL', 'Paesi Bassi', '+31'], ['PK', 'Pakistan', '+92'],
  ['PW', 'Palau', '+680'], ['PA', 'Panama', '+507'], ['PG', 'Papua Nuova Guinea', '+675'],
  ['PY', 'Paraguay', '+595'], ['PE', 'Perù', '+51'], ['PL', 'Polonia', '+48'],
  ['PT', 'Portogallo', '+351'], ['QA', 'Qatar', '+974'], ['GB', 'Regno Unito', '+44'],
  ['CF', 'Repubblica Centrafricana', '+236'], ['CZ', 'Repubblica Ceca', '+420'],
  ['CD', 'Repubblica Democratica del Congo', '+243'], ['DO', 'Repubblica Dominicana', '+1809'],
  ['RO', 'Romania', '+40'], ['RW', 'Ruanda', '+250'], ['RU', 'Russia', '+7'],
  ['KN', 'Saint Kitts e Nevis', '+1869'], ['LC', 'Saint Lucia', '+1758'],
  ['VC', 'Saint Vincent e Grenadine', '+1784'], ['WS', 'Samoa', '+685'], ['SM', 'San Marino', '+378'],
  ['ST', 'São Tomé e Príncipe', '+239'], ['SA', 'Arabia Saudita', '+966'], ['SN', 'Senegal', '+221'],
  ['RS', 'Serbia', '+381'], ['SC', 'Seychelles', '+248'], ['SL', 'Sierra Leone', '+232'],
  ['SG', 'Singapore', '+65'], ['SY', 'Siria', '+963'], ['SK', 'Slovacchia', '+421'],
  ['SI', 'Slovenia', '+386'], ['SB', 'Isole Salomone', '+677'], ['SO', 'Somalia', '+252'],
  ['ZA', 'Sudafrica', '+27'], ['SS', 'Sud Sudan', '+211'], ['ES', 'Spagna', '+34'],
  ['LK', 'Sri Lanka', '+94'], ['US', 'Stati Uniti', '+1'], ['SD', 'Sudan', '+249'],
  ['SR', 'Suriname', '+597'], ['SE', 'Svezia', '+46'], ['CH', 'Svizzera', '+41'],
  ['TJ', 'Tagikistan', '+992'], ['TW', 'Taiwan', '+886'], ['TZ', 'Tanzania', '+255'],
  ['TH', 'Thailandia', '+66'], ['TL', 'Timor-Leste', '+670'], ['TG', 'Togo', '+228'],
  ['TO', 'Tonga', '+676'], ['TT', 'Trinidad e Tobago', '+1868'], ['TN', 'Tunisia', '+216'],
  ['TR', 'Turchia', '+90'], ['TM', 'Turkmenistan', '+993'], ['TV', 'Tuvalu', '+688'],
  ['UA', 'Ucraina', '+380'], ['UG', 'Uganda', '+256'], ['HU', 'Ungheria', '+36'],
  ['UY', 'Uruguay', '+598'], ['UZ', 'Uzbekistan', '+998'], ['VU', 'Vanuatu', '+678'],
  ['VA', 'Città del Vaticano', '+379'], ['VE', 'Venezuela', '+58'], ['VN', 'Vietnam', '+84'],
  ['YE', 'Yemen', '+967'], ['ZM', 'Zambia', '+260'], ['ZW', 'Zimbabwe', '+263'],
];

/** Stati membri UE (27) — determina variante fiscale 'eu' e disponibilità VIES. */
const EU_ISO = new Set([
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR',
  'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
]);

/** Label adattive dove il termine generico non è quello giusto. */
const ADMIN_LABELS: Record<string, string> = {
  IT: 'Provincia', DE: 'Bundesland', AT: 'Bundesland', CH: 'Cantone',
  US: 'Stato', GB: 'Contea', FR: 'Dipartimento', ES: 'Provincia',
  CA: 'Provincia', AU: 'Stato', BR: 'Stato', NL: 'Provincia', BE: 'Provincia',
};
const POSTAL_LABELS: Record<string, string> = {
  IT: 'CAP', DE: 'PLZ', AT: 'PLZ', CH: 'NPA', US: 'ZIP code', GB: 'Postcode',
};
/** Prefisso VIES: coincide con l'ISO tranne la Grecia (EL). */
const VIES_PREFIX_OVERRIDES: Record<string, string> = { GR: 'EL' };

export const COUNTRIES: CountryInfo[] = ROWS.map(([iso, name, phonePrefix]) => ({
  iso,
  name,
  phonePrefix,
  eu: EU_ISO.has(iso),
  adminLabel: ADMIN_LABELS[iso] ?? 'Provincia / Stato',
  postalLabel: POSTAL_LABELS[iso] ?? 'Codice postale',
  vatPrefix: EU_ISO.has(iso) ? (VIES_PREFIX_OVERRIDES[iso] ?? iso) : null,
}));

const BY_ISO = new Map(COUNTRIES.map((c) => [c.iso, c]));
const BY_NAME = new Map(COUNTRIES.map((c) => [c.name.toLowerCase(), c]));
/** Sinonimi tollerati nei dati storici (clients.country è testo libero). */
const NAME_SYNONYMS: Record<string, string> = {
  italy: 'IT', it: 'IT', italia: 'IT',
  germany: 'DE', france: 'FR', spain: 'ES', switzerland: 'CH',
  'united kingdom': 'GB', uk: 'GB', usa: 'US', 'united states': 'US',
};

export function getCountryByIso(iso: string | null | undefined): CountryInfo | null {
  if (!iso) return null;
  return BY_ISO.get(iso.trim().toUpperCase()) ?? null;
}

export function getCountryByName(name: string | null | undefined): CountryInfo | null {
  if (!name) return null;
  const key = name.trim().toLowerCase();
  if (!key) return null;
  const direct = BY_NAME.get(key);
  if (direct) return direct;
  const synonym = NAME_SYNONYMS[key];
  return synonym ? (BY_ISO.get(synonym) ?? null) : null;
}

export function isoFromCountryName(name: string | null | undefined): string | null {
  return getCountryByName(name)?.iso ?? null;
}

// Prefissi P.IVA riconosciuti (UE + CH/GB). Solo questi: un testo sporco nel
// campo IVA non deve trasformarsi per sbaglio in un "paese estero".
// 'EL' = Grecia, 'XI' = Irlanda del Nord (regime GB).
const VAT_PREFIX_TO_ISO: Record<string, string> = {
  AT: 'AT', BE: 'BE', BG: 'BG', HR: 'HR', CY: 'CY', CZ: 'CZ', DK: 'DK',
  EE: 'EE', FI: 'FI', FR: 'FR', DE: 'DE', EL: 'GR', HU: 'HU', IE: 'IE',
  IT: 'IT', LV: 'LV', LT: 'LT', LU: 'LU', MT: 'MT', NL: 'NL', PL: 'PL',
  PT: 'PT', RO: 'RO', SK: 'SK', SI: 'SI', ES: 'ES', SE: 'SE',
  CH: 'CH', GB: 'GB', XI: 'GB',
};

/**
 * Deriva il paese dal prefisso della P.IVA in formato UE ("FR79988523304").
 * Serve come fallback quando il campo country del cliente è vuoto: senza
 * questo, un cliente chiaramente estero ricade nelle regole italiane
 * (SDI/PEC/CF richiesti a torto nel gate contratto).
 */
export function countryFromVatNumber(vat: string | null | undefined): CountryInfo | null {
  if (!vat) return null;
  const m = vat.trim().toUpperCase().match(/^([A-Z]{2})[0-9A-Z]/);
  if (!m) return null;
  const iso = VAT_PREFIX_TO_ISO[m[1]];
  return iso ? getCountryByIso(iso) : null;
}

/** Lista dei nomi per le select Paese (stesso ordine storico alfabetico ISO-name). */
export const COUNTRY_NAMES: string[] = COUNTRIES.map((c) => c.name)
  .sort((a, b) => a.localeCompare(b, 'it'));

export function isItalyName(name: string | null | undefined): boolean {
  return getCountryByName(name)?.iso === 'IT';
}

/**
 * Riconosce un paese estero: nome presente in lista e diverso dall'Italia.
 * Nomi vuoti o non riconosciuti NON sono "esteri" (default conservativo:
 * il gate contratto e i campi fiscali restano quelli italiani).
 */
export function isRecognizedForeignCountry(name: string | null | undefined): boolean {
  const c = getCountryByName(name);
  return !!c && c.iso !== 'IT';
}

export type FiscalVariant = 'it' | 'eu' | 'extra';

/**
 * Variante fiscale del form: 'it' (Italia, vuoto o non riconosciuto),
 * 'eu' (UE non Italia: P.IVA con prefisso + VIES), 'extra' (identificativo
 * fiscale generico, niente VIES).
 */
export function fiscalVariantForCountry(name: string | null | undefined): FiscalVariant {
  const c = getCountryByName(name);
  if (!c || c.iso === 'IT') return 'it';
  return c.eu ? 'eu' : 'extra';
}

export function adminDivisionLabel(name: string | null | undefined): string {
  return getCountryByName(name)?.adminLabel ?? 'Provincia / Stato';
}

export function postalCodeLabel(name: string | null | undefined): string {
  return getCountryByName(name)?.postalLabel ?? 'Codice postale';
}

export function phonePrefixForCountry(name: string | null | undefined): string | null {
  return getCountryByName(name)?.phonePrefix ?? null;
}

export function viesPrefixForCountry(name: string | null | undefined): string | null {
  return getCountryByName(name)?.vatPrefix ?? null;
}
